import type { ChatMessage } from "../../shared/protocol";
import { getClient, type ChatClient, type ChatState } from "./client";
import { renderMarkdown } from "./markdown";
import { styles, themeCss } from "./styles";
import { turnstileToken } from "./turnstile";

// <conversational-agent mode="launcher|inline" site="…" api="…">
//   launcher: a button in the bottom-right corner that opens a chat panel.
//   inline:   a full conversation that fills its container, like ChatGPT or Claude.
//             With the compact attribute it is just a message box on the page; focusing
//             it morphs the box into a dialog (fullscreen on phones) that holds the chat.
// Optional: theme="light|dark", accent="#hex", heading, placeholder,
// suggestions="First question|Second question".

const ICON_CHAT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a8.5 8.5 0 0 1-12.4 7.6L3 21l1.4-5.1A8.5 8.5 0 1 1 21 12z"/></svg>`;
const ICON_DOWN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>`;
const ICON_CLOSE = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const ICON_NEW = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>`;
const NEW_CHAT_BTN = `<button class="icon-btn new-chat" type="button" aria-label="Start a new chat" title="Start a new chat" hidden>${ICON_NEW}</button>`;
const ICON_ASK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a8.5 8.5 0 0 1-12.4 7.6L3 21l1.4-5.6A8.5 8.5 0 1 1 21 12z"/></svg>`;
const ICON_SEND = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>`;
const ICON_SPARK = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.5c.4 3.9 1.5 6.1 3.2 7.3 1.4 1 3.4 1.6 6.3 2.2-2.9.6-4.9 1.2-6.3 2.2-1.7 1.2-2.8 3.4-3.2 7.3-.4-3.9-1.5-6.1-3.2-7.3-1.4-1-3.4-1.6-6.3-2.2 2.9-.6 4.9-1.2 6.3-2.2 1.7-1.2 2.8-3.4 3.2-7.3z"/></svg>`;

export interface Defaults {
  site?: string;
  api?: string;
}

export const defaults: Defaults = {};

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export class ConversationalAgent extends HTMLElement {
  private client?: ChatClient;
  private unsubscribe?: () => void;
  private root: ShadowRoot;
  private log!: HTMLElement;
  private input!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private errorEl!: HTMLElement;
  private panel?: HTMLElement;
  private launcherBtn?: HTMLButtonElement;
  private rendered = new Map<string, { el: HTMLElement; text: string }>();
  private unread = 0;
  private seen = new Set<string>();
  private open = false;
  private state?: ChatState;
  private dialog?: HTMLDialogElement;
  private card?: HTMLElement;
  private trigger?: HTMLElement;
  private triggerInput?: HTMLInputElement;
  private morphing: Animation[] = [];
  private unlockScroll?: () => void;
  // The phone keyboard shrinks the visual viewport; keep the card (and its
  // composer) inside it. Changes during a morph are applied when it ends.
  private onViewport = () => {
    if (this.open && !this.morphing.length) this.placeCard(this.sheetRect());
  };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  get mode(): "launcher" | "inline" {
    return this.getAttribute("mode") === "launcher" ? "launcher" : "inline";
  }

  get compact(): boolean {
    return this.mode === "inline" && this.hasAttribute("compact");
  }

  connectedCallback() {
    const site = this.getAttribute("site") || defaults.site;
    const api = (this.getAttribute("api") || defaults.api || "").replace(/\/$/, "");
    if (!site || !api) {
      console.warn("conversational-agent: missing site or api attribute");
      return;
    }
    const accent = this.getAttribute("accent");
    this.root.innerHTML = `<style>${styles}${themeCss}${accent ? `:host{--ca-accent:${esc(accent)}}` : ""}</style>${
      this.mode === "launcher" ? this.launcherHtml() : this.inlineHtml()
    }`;
    // In compact mode the conversation lives in the dialog's card.
    const scope = this.compact ? this.root.querySelector(".card")! : this.root;
    this.log = scope.querySelector(".log")!;
    this.input = scope.querySelector("textarea")!;
    this.sendBtn = scope.querySelector(".send")!;
    this.errorEl = scope.querySelector(".error")!;
    this.wireComposer(scope.querySelector("form")!);
    if (this.mode === "launcher") this.wireLauncher();
    if (this.compact) this.wireSheet();
    this.root.querySelectorAll(".new-chat").forEach((b) => b.addEventListener("click", () => this.newChat()));

    this.client = getClient(api, site);
    this.client.challenge ??= this.challenge;
    if (this.compact) this.client.reveal ??= this.reveal;
    this.unsubscribe = this.client.subscribe((s) => this.render(s));
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.rendered.clear();
    if (this.client?.challenge === this.challenge) this.client.challenge = undefined;
    if (this.client?.reveal === this.reveal) this.client.reveal = undefined;
    this.unlockScroll?.();
    this.unlockScroll = undefined;
    window.removeEventListener("resize", this.onViewport);
    window.visualViewport?.removeEventListener("resize", this.onViewport);
    window.visualViewport?.removeEventListener("scroll", this.onViewport);
  }

  // The challenge renders in light DOM and shows above the composer through a slot.
  private async solveChallenge(siteKey: string): Promise<string> {
    const host = document.createElement("div");
    host.slot = "turnstile";
    this.appendChild(host);
    try {
      return await turnstileToken(siteKey, host);
    } finally {
      setTimeout(() => host.remove(), 0);
    }
  }

  private challenge = (siteKey: string) => this.solveChallenge(siteKey);
  // On phones the keyboard is still closing when the dialog is gone; a page that
  // scrolls before the viewport settles gets its scroll cut short.
  private reveal = async () => {
    await this.closeSheet();
    await viewportSettled();
  };

  private launcherHtml() {
    return `
      <div class="panel" role="dialog" aria-label="Chat" hidden>
        <header>
          <div class="avatar ai">${ICON_SPARK}</div>
          <div class="who"><div class="title"></div><div class="status"><span class="dot"></span><span class="status-text"></span></div></div>
          ${NEW_CHAT_BTN}
          <button class="icon-btn close" type="button" aria-label="Close chat">${ICON_CLOSE}</button>
        </header>
        <div class="log" aria-live="polite"></div>
        <div class="error" hidden></div>
        <div class="challenge"><slot name="turnstile"></slot></div>
        ${this.composerHtml()}
      </div>
      <button class="launcher" type="button" aria-label="Open chat" aria-expanded="false">${ICON_CHAT}<span class="badge" hidden></span></button>`;
  }

  private inlineHtml() {
    if (this.compact) return this.compactHtml();
    return `
      <div class="inline">
        <div class="log" aria-live="polite"></div>
        <div class="error" hidden></div>
        <div class="challenge"><slot name="turnstile"></slot></div>
        ${this.composerHtml()}
        <div class="foot status-line"><span class="dot"></span><span class="status-text"></span><button class="new-chat link-btn" type="button" hidden>New chat</button></div>
      </div>`;
  }

  private compactHtml() {
    return `
      <div class="inline compact">
        <div class="trigger">
          <div class="field">
            <input type="text" aria-label="Open chat" aria-haspopup="dialog" autocomplete="off" placeholder="${esc(this.placeholder)}">
            <span class="send" aria-hidden="true">${ICON_SEND}</span>
          </div>
        </div>
        <div class="chips-slot"></div>
      </div>
      <dialog class="sheet" aria-label="Chat">
        <div class="scrim"></div>
        <div class="card">
          <div class="card-bg"></div>
          <header>
            <div class="avatar ai">${ICON_SPARK}</div>
            <div class="who"><div class="title"></div><div class="status"><span class="dot"></span><span class="status-text"></span></div></div>
            ${NEW_CHAT_BTN}
            <button class="icon-btn close" type="button" aria-label="Close chat">${ICON_CLOSE}</button>
          </header>
          <div class="log" aria-live="polite"></div>
          <div class="error" hidden></div>
          <div class="challenge"><slot name="turnstile"></slot></div>
          ${this.composerHtml()}
        </div>
      </dialog>`;
  }

  private get placeholder() {
    return this.getAttribute("placeholder") || "Ask anything…";
  }

  private composerHtml() {
    return `
      <form class="composer">
        <div class="field">
          <textarea rows="1" aria-label="Message" placeholder="${esc(this.placeholder)}"></textarea>
          <button class="send" type="submit" aria-label="Send" disabled>${ICON_SEND}</button>
        </div>
      </form>`;
  }

  private wireComposer(form: HTMLFormElement) {
    const resize = () => {
      this.input.style.height = "auto";
      this.input.style.height = `${Math.min(this.input.scrollHeight, 160)}px`;
      this.sendBtn.disabled = !this.input.value.trim();
    };
    this.input.addEventListener("input", resize);
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = this.input.value;
      if (!text.trim()) return;
      this.input.value = "";
      resize();
      this.client?.send(text);
    });
  }

  private wireLauncher() {
    this.panel = this.root.querySelector(".panel")!;
    this.launcherBtn = this.root.querySelector(".launcher")!;
    this.launcherBtn.addEventListener("click", () => this.setOpen(!this.open));
    this.root.querySelector(".close")!.addEventListener("click", () => this.setOpen(false));
    this.root.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape" && this.open) this.setOpen(false);
    });
    // Stay open across page navigations within the site.
    try {
      if (sessionStorage.getItem("conversationalagent:open") === "1") this.setOpen(true, false);
    } catch {
      // storage blocked
    }
  }

  private setOpen(open: boolean, focus = true) {
    this.open = open;
    this.panel!.hidden = !open;
    this.launcherBtn!.classList.toggle("open", open);
    this.launcherBtn!.setAttribute("aria-expanded", String(open));
    this.launcherBtn!.setAttribute("aria-label", open ? "Close chat" : "Open chat");
    this.launcherBtn!.innerHTML = `${open ? ICON_DOWN : ICON_CHAT}<span class="badge" hidden></span>`;
    try {
      sessionStorage.setItem("conversationalagent:open", open ? "1" : "0");
    } catch {
      // storage blocked
    }
    if (open) {
      this.unread = 0;
      this.log.scrollTop = this.log.scrollHeight;
      if (focus) this.input.focus();
    }
    this.updateBadge();
  }

  // ---------- compact: the box on the page morphs into a dialog ----------

  private wireSheet() {
    this.dialog = this.root.querySelector("dialog")!;
    this.card = this.root.querySelector(".card")!;
    this.trigger = this.root.querySelector(".trigger")!;
    this.triggerInput = this.trigger.querySelector("input")!;
    // Moving focus inside the user's tap keeps the phone keyboard up.
    let suppress = false;
    this.triggerInput.addEventListener("focus", () => !suppress && this.openSheet());
    this.triggerInput.addEventListener("click", () => this.openSheet());
    this.triggerInput.addEventListener("keydown", (e) => {
      if (e.key !== "Tab" && e.key !== "Shift" && e.key !== "Escape") this.openSheet();
    });
    this.dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.closeSheet();
    });
    this.root.querySelector(".close")!.addEventListener("click", () => this.closeSheet());
    this.root.querySelector(".scrim")!.addEventListener("click", () => this.closeSheet());
    this.dialog.addEventListener("close", () => {
      // Focus returns to the box on the page; don't let that reopen the dialog.
      suppress = true;
      setTimeout(() => (suppress = false));
    });
  }

  private get fullscreen() {
    return matchMedia("(max-width: 640px)").matches;
  }

  private sheetRect(): Rect {
    const vv = window.visualViewport;
    if (this.fullscreen) {
      // Follow the visual viewport so the composer stays above the keyboard.
      return { left: 0, top: vv?.offsetTop ?? 0, width: innerWidth, height: vv?.height ?? innerHeight, radius: 0 };
    }
    const width = Math.min(720, innerWidth - 48);
    const height = Math.min(720, innerHeight - 96);
    return { left: (innerWidth - width) / 2, top: (innerHeight - height) / 2, width, height, radius: 24 };
  }

  private placeCard(r: Rect) {
    Object.assign(this.card!.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, borderRadius: `${r.radius}px` });
  }

  private triggerRect(): Rect {
    const field = this.trigger!.querySelector<HTMLElement>(".field")!;
    const b = field.getBoundingClientRect();
    return { left: b.left, top: b.top, width: b.width, height: b.height, radius: parseFloat(getComputedStyle(field).borderTopLeftRadius) || 0 };
  }

  private openSheet() {
    if (this.open || !this.dialog) return;
    this.settle();
    this.open = true;
    this.unread = 0;
    const from = this.triggerRect();
    this.unlockScroll ??= lockScroll();
    if (!this.dialog.open) this.dialog.showModal();
    this.input.focus({ preventScroll: true });
    this.trigger!.style.visibility = "hidden";
    const to = this.sheetRect();
    this.placeCard(to);
    this.log.scrollTop = this.log.scrollHeight;
    window.addEventListener("resize", this.onViewport);
    window.visualViewport?.addEventListener("resize", this.onViewport);
    window.visualViewport?.addEventListener("scroll", this.onViewport);
    this.updateBadge();
    this.morph(from, to, true).then(() => {
      if (!this.open) return;
      // The keyboard usually opens while the card is still growing.
      const now = this.sheetRect();
      if (now.top !== to.top || now.height !== to.height || now.width !== to.width) this.placeCard(now);
      this.log.scrollTop = this.log.scrollHeight;
    });
  }

  private async closeSheet() {
    if (!this.open || !this.dialog) return;
    this.settle();
    this.open = false;
    window.removeEventListener("resize", this.onViewport);
    window.visualViewport?.removeEventListener("resize", this.onViewport);
    window.visualViewport?.removeEventListener("scroll", this.onViewport);
    const from = this.triggerRect();
    const to = this.sheetRect();
    this.input.blur();
    await this.morph(from, to, false);
    if (this.open) return;
    this.dialog.close();
    this.trigger!.style.visibility = "";
    this.unlockScroll?.();
    this.unlockScroll = undefined;
  }

  /** Finish a running morph at once, so a new one starts from a resting state. */
  private settle() {
    for (const a of this.morphing.splice(0)) a.finish();
  }

  // The card starts as an exact copy of the box on the page (same place, size,
  // corners, fill and padding) and grows into the dialog; the composer rides
  // along at its bottom, so the box itself appears to move into place.
  private async morph(field: Rect, sheet: Rect, opening: boolean) {
    const card = this.card!;
    const composer = card.querySelector<HTMLElement>(".composer")!;
    const cardField = card.querySelector<HTMLElement>(".field")!;
    const pageField = this.trigger!.querySelector<HTMLElement>(".field")!;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

    const rect = (r: Rect) => ({ left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, borderRadius: `${r.radius}px` });
    const FIELD_PROPS = ["backgroundColor", "boxShadow", "borderColor", "borderRadius", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft"] as const;
    const look = (el: HTMLElement) => {
      const cs = getComputedStyle(el);
      return Object.fromEntries(FIELD_PROPS.map((p) => [p, cs[p]]));
    };
    const pad = (el: HTMLElement) => {
      const cs = getComputedStyle(el);
      return { paddingTop: cs.paddingTop, paddingRight: cs.paddingRight, paddingBottom: cs.paddingBottom, paddingLeft: cs.paddingLeft };
    };
    const small = { card: rect(field), field: look(pageField), composer: { paddingTop: "0px", paddingRight: "0px", paddingBottom: "0px", paddingLeft: "0px" } };
    const big = { card: rect(sheet), field: look(cardField), composer: pad(composer) };
    const [a, b] = opening ? [small, big] : [big, small];

    const timing: KeyframeAnimationOptions = reduced
      ? { duration: 160, easing: "ease" }
      : opening
        ? { duration: 620, easing: SPRING }
        : { duration: 380, easing: "cubic-bezier(.32,.72,0,1)" };
    const fadeIn = opening
      ? [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 0, transform: "translateY(10px)", offset: 0.25 }, { opacity: 1, transform: "none" }]
      : [{ opacity: 1 }, { opacity: 0, offset: 0.35 }, { opacity: 0 }];
    const run = (el: Element | null, frames: Keyframe[]) => el && this.morphing.push(el.animate(frames, { ...timing, fill: "both" }));

    if (reduced) {
      run(card, opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }]);
    } else {
      run(card, [a.card, b.card]);
      run(composer, [a.composer, b.composer]);
      run(cardField, [a.field, b.field]);
      run(card.querySelector(".card-bg"), opening ? [{ opacity: 0 }, { opacity: 1, offset: 0.3 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 1, offset: 0.6 }, { opacity: 0 }]);
      for (const el of card.querySelectorAll("header, .log, .error")) run(el, fadeIn);
    }
    run(this.dialog!.querySelector(".scrim"), opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }]);

    const mine = [...this.morphing];
    await Promise.all(mine.map((x) => x.finished.catch(() => undefined)));
    for (const x of mine) x.cancel();
    this.morphing = this.morphing.filter((x) => !mine.includes(x));
  }

  private updateBadge() {
    if (this.triggerInput) {
      const unread = !this.open && this.unread > 0;
      this.trigger!.classList.toggle("unread", unread);
      const hasChat = !!this.state?.messages.length;
      this.triggerInput.placeholder = unread
        ? `New reply from ${this.state?.mode === "human" ? this.state.agentName || this.state.ownerName : "the assistant"}. Open to read…`
        : hasChat
          ? "Continue the conversation…"
          : this.placeholder;
    }
    const badge = this.launcherBtn?.querySelector<HTMLElement>(".badge");
    if (!badge) return;
    badge.hidden = this.open || this.unread === 0;
    badge.textContent = String(this.unread);
  }

  /** Clears the chat for the visitor; the next message starts a new conversation. */
  private newChat() {
    if (!confirm("Start a new chat? This conversation will be cleared.")) return;
    this.client?.reset();
    this.input.focus();
  }

  private render(s: ChatState) {
    const first = !this.state;
    this.state = s;
    const nearBottom = this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < 80;

    // Header and status line
    const live = s.mode === "human";
    const statusText = live ? `${s.agentName || s.ownerName} is here` : s.ownerName ? `AI assistant · ${s.ownerName} can join` : "AI assistant";
    this.root.querySelectorAll(".status-text").forEach((el) => (el.textContent = statusText));
    this.root.querySelectorAll(".dot").forEach((el) => el.classList.toggle("on", live));
    this.root.querySelectorAll<HTMLElement>(".new-chat").forEach((el) => (el.hidden = !s.messages.length));
    const title = this.root.querySelector(".title");
    if (title) title.textContent = s.siteName || "Chat";

    this.errorEl.hidden = !s.error;
    this.errorEl.textContent = s.error ?? "";

    // Messages, keyed by id so streaming only touches the last bubble.
    const items: ChatMessage[] = [...s.messages, ...s.pending.filter((p) => !s.messages.some((m) => m.id === p.id))];
    if (s.draft) items.push(s.draft);
    // A new message (not a streaming update) always scrolls into view.
    const added = items.some((m) => !this.rendered.has(m.id));
    const wanted = new Set(items.map((m) => m.id));
    for (const [id, r] of this.rendered) {
      if (!wanted.has(id)) {
        r.el.remove();
        this.rendered.delete(id);
      }
    }
    this.log.querySelectorAll(".hero, .greeting, .typing-row").forEach((el) => el.remove());

    if (this.compact) {
      // Suggestions stay under the box on the page, also once the chat has started.
      const slot = this.root.querySelector(".chips-slot")!;
      if (!slot.childElementCount) slot.append(this.emptyState(s, true));
    }
    if (!items.length) this.log.prepend(this.emptyState(s));

    let prev: HTMLElement | null = null;
    for (const m of items) {
      let r = this.rendered.get(m.id);
      if (!r) {
        r = { el: document.createElement("div"), text: "" };
        this.rendered.set(m.id, r);
      }
      if (r.text !== m.text || !r.el.className) {
        r.el.className = `msg ${m.role}`;
        r.el.innerHTML = this.messageHtml(m);
        r.text = m.text;
      }
      const next: ChildNode | null = prev ? prev.nextSibling : this.log.firstChild;
      if (next !== r.el) this.log.insertBefore(r.el, next);
      prev = r.el;

      if (!this.seen.has(m.id)) {
        this.seen.add(m.id);
        if (!first && !this.open && (m.role === "assistant" || m.role === "owner") && m.id !== s.draft?.id) this.unread++;
      }
    }

    if (s.typing && !s.draft) {
      const row = document.createElement("div");
      row.className = `msg ${s.typing} typing-row`;
      row.innerHTML = `${this.avatarHtml(s.typing, s.agentName)}<div class="bubble typing"><span></span><span></span><span></span></div>`;
      this.log.appendChild(row);
    }

    this.updateBadge();
    if (nearBottom || first || added) this.log.scrollTop = this.log.scrollHeight;
  }

  private emptyState(s: ChatState, chipsOnly = false): HTMLElement {
    const el = document.createElement("div");
    const suggestions = (this.getAttribute("suggestions") || "").split("|").map((x) => x.trim()).filter(Boolean);
    const chips = suggestions.length
      ? `<div class="chips">${suggestions.map((q) => `<button type="button" class="chip">${ICON_ASK}<span>${esc(q)}</span></button>`).join("")}</div>`
      : "";
    if (chipsOnly) {
      el.innerHTML = chips;
    } else if (this.mode === "inline" && !this.compact) {
      el.className = "hero";
      const heading = this.getAttribute("heading") || s.greeting || "How can I help?";
      el.innerHTML = `<h2>${esc(heading)}</h2>${chips}`;
    } else {
      el.className = "greeting";
      el.innerHTML = `<div class="msg assistant">${this.avatarHtml("assistant")}<div class="bubble md">${renderMarkdown(s.greeting || "Hi! How can I help?")}</div></div>${chips}`;
      el.style.cssText = "display:flex;flex-direction:column;gap:12px";
    }
    el.querySelectorAll<HTMLButtonElement>(".chip").forEach((b) =>
      b.addEventListener("click", () => {
        if (this.compact) this.openSheet();
        this.client?.send(b.textContent || "");
      }),
    );
    return el;
  }

  private avatarHtml(role: "assistant" | "owner", name?: string) {
    if (role === "owner") return `<div class="avatar owner">${esc((name || "?").charAt(0).toUpperCase())}</div>`;
    return `<div class="avatar ai">${ICON_SPARK}</div>`;
  }

  private messageHtml(m: ChatMessage): string {
    if (m.role === "visitor") return `<div class="bubble">${esc(m.text)}</div>`;
    if (m.role === "system") return `<span>${esc(m.text)}</span>`;
    if (m.role === "owner") {
      return `${this.avatarHtml("owner", m.author)}<div class="body"><div class="name">${esc(m.author || "Owner")}</div><div class="bubble md">${renderMarkdown(m.text)}</div></div>`;
    }
    return `${this.avatarHtml("assistant")}<div class="bubble md">${renderMarkdown(m.text)}</div>`;
  }
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: number;
}

// A lightly damped spring (about 2% overshoot), as a CSS linear() easing.
const SPRING = (() => {
  if (typeof CSS === "undefined" || !CSS.supports("animation-timing-function", "linear(0, 1)")) return "cubic-bezier(.32,.72,0,1)";
  const zeta = 0.78;
  const omega = 10;
  const wd = omega * Math.sqrt(1 - zeta * zeta);
  const points: string[] = [];
  for (let i = 0; i <= 60; i++) {
    const t = i / 60;
    const x = i === 60 ? 1 : 1 - Math.exp(-zeta * omega * t) * (Math.cos(wd * t) + ((zeta * omega) / wd) * Math.sin(wd * t));
    points.push(x.toFixed(4));
  }
  return `linear(${points.join(", ")})`;
})();

/** Resolves once the visual viewport (keyboard, toolbars) has stopped changing, or after a second. */
function viewportSettled(): Promise<void> {
  const vv = window.visualViewport;
  if (!vv) return Promise.resolve();
  return new Promise((resolve) => {
    const end = performance.now() + 1000;
    let last = `${vv.height}|${vv.offsetTop}|${scrollY}`;
    let stable = 0;
    const tick = () => {
      const now = `${vv.height}|${vv.offsetTop}|${scrollY}`;
      stable = now === last ? stable + 1 : 0;
      last = now;
      if (stable >= 6 || performance.now() > end) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Stops the page behind the dialog from scrolling; returns the undo. Pinning
 * the body in place is what stops iOS Safari, which ignores overflow: hidden
 * and scrolls the page when the keyboard opens.
 */
function lockScroll(): () => void {
  const html = document.documentElement;
  const body = document.body;
  const y = scrollY;
  const gap = innerWidth - html.clientWidth;
  const prev = body.style.cssText;
  const prevHtml = html.style.overflow;
  Object.assign(body.style, { position: "fixed", top: `${-y}px`, left: "0", right: "0", width: "100%" });
  if (gap > 0) body.style.paddingRight = `${gap}px`;
  html.style.overflow = "hidden";
  return () => {
    body.style.cssText = prev;
    html.style.overflow = prevHtml;
    // Jump straight back, even on sites with smooth scrolling.
    const behavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    scrollTo(0, y);
    html.style.scrollBehavior = behavior;
  };
}
